/**
 * CommunityMediaSection Component
 *
 * Display and manage community media (images and videos) with full CRUD operations.
 * UI matches MediaUploadSection exactly with grid for images and list for videos.
 * Uses React Query hooks for real-time server synchronization.
 */

import {
  ArrowUpToLine,
  Image,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Video as VideoIcon,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { AiImageGenerator, type EntityContext } from "@/components/media/AiImageGenerator"
import { MediaUploadModal } from "@/components/media/MediaUploadModal"
import { MediaPreview } from "@/components/media/mediaPreview"
import { MEDIA_UPLOAD_LIMITS } from "@/constants/media"
import { isValidVideoUrl as checkIsValidVideoUrl, fetchVideoMetadata } from "@/utils/videoThumbnail"
import { useCommunityImages } from "../hooks/useCommunityImages"
import { useDeleteCommunityImage } from "../hooks/useDeleteCommunityImage"
import { useSetCommunityCoverImage } from "../hooks/useSetCommunityCoverImage"
import { useUploadCommunityImage } from "../hooks/useUploadCommunityImage"
import { uploadVideoThumbnail } from "../services/community.service"

interface Photo {
  id: string
  src: string
  isCover?: boolean
}

interface VideoItem {
  id: string
  title: string
  url: string
}

interface VideoUrlEntry {
  id: string
  url: string
  thumbnail: string
  isEditing?: boolean
  title?: string
  isProcessed?: boolean // Track if metadata was attempted to fetch
  isInvalid?: boolean // Track if URL is invalid (non-YouTube/non-Vimeo)
}

interface CommunityMediaSectionProps {
  communityId: string
  onMediaCountChange?: (imageCount: number, videoCount: number) => void
  onCoverImageChange?: (hasCoverImage: boolean) => void
  initialVideoUrls?: VideoUrlEntry[]
  onVideoUrlsChange?: (videos: VideoUrlEntry[]) => void
  maxFiles?: number
  maxFileSize?: number
  enableAiGeneration?: boolean
  aiEntityType?: "ritual" | "community" | "coaching"
  aiEntityContext?: EntityContext
}

// Validation limits
export const COMMUNITY_IMAGE_LIMITS = {
  MAX_IMAGES: 5,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ["image/png", "image/jpeg", "image/jpg"],
} as const

const COMMUNITY_VIDEO_LIMITS = {
  MAX_VIDEOS: MEDIA_UPLOAD_LIMITS.MAX_VIDEOS,
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_TYPES: ["video/mp4", "video/webm"],
} as const

export function CommunityMediaSection({
  communityId,
  onMediaCountChange,
  onCoverImageChange,
  initialVideoUrls = [],
  onVideoUrlsChange,
  maxFiles = COMMUNITY_IMAGE_LIMITS.MAX_IMAGES,
  maxFileSize = COMMUNITY_IMAGE_LIMITS.MAX_FILE_SIZE,
  enableAiGeneration = false,
  aiEntityType,
  aiEntityContext,
}: CommunityMediaSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Debounce timers for video thumbnail generation
  const debounceTimersRef = useRef<Record<number, NodeJS.Timeout>>({})

  // Queries and mutations
  const { data: community, isLoading } = useCommunityImages(communityId)
  const uploadImage = useUploadCommunityImage()
  const deleteImage = useDeleteCommunityImage()
  const setCoverImage = useSetCommunityCoverImage()

  const [isUploading, setIsUploading] = useState(false)
  const [videoUrls, setVideoUrls] = useState<VideoUrlEntry[]>(
    initialVideoUrls.length > 0
      ? initialVideoUrls
      : [{ id: "video-0", url: "", thumbnail: "", title: "", isProcessed: false }],
  )
  const [urlErrors, setUrlErrors] = useState<Record<number, string | null>>({})
  const [uploadingThumbnailIndex, setUploadingThumbnailIndex] = useState<number | null>(null)
  const [thumbnailError, setThumbnailError] = useState<string | null>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false)

  // Sync video URLs when initialVideoUrls prop changes (e.g., after fetching community data)
  useEffect(() => {
    if (initialVideoUrls && initialVideoUrls.length > 0) {
      setVideoUrls(initialVideoUrls)
    }
  }, [initialVideoUrls])

  // Focus input when entering edit mode
  useEffect(() => {
    videoUrls.forEach((video, index) => {
      if (video.isEditing) {
        const input = document.getElementById(`videoUrl-${index}`)
        if (input) {
          input.focus()
        }
      }
    })
  }, [videoUrls])

  // Handle video URL change at specific index - receives event from CustomInput
  const handleVideoUrlChange = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newUrl = e.target.value

    // Clear existing timer for this index
    if (debounceTimersRef.current[index]) {
      clearTimeout(debounceTimersRef.current[index])
    }

    // Update URL immediately
    setVideoUrls(prev => {
      const updated = [...prev]
      if (updated[index]) {
        updated[index] = { ...updated[index], url: newUrl }
      }
      return updated
    })

    // Validate URL - show error if not empty and not a valid video URL
    if (newUrl && !checkIsValidVideoUrl(newUrl)) {
      setUrlErrors(prev => ({ ...prev, [index]: "Please enter a valid YouTube or Vimeo URL" }))

      // Mark as invalid to keep input visible and prevent useEffect from fetching
      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], isInvalid: true, isProcessed: true }
        }
        return updated
      })
      return
    }

    // Clear error if URL is empty or valid
    if (urlErrors[index]) {
      setUrlErrors(prev => ({ ...prev, [index]: null }))
    }

    // Clear invalid flag and reset isProcessed when URL changes (to allow re-fetching)
    if (!newUrl || checkIsValidVideoUrl(newUrl)) {
      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], isInvalid: false, isProcessed: false }
        }
        return updated
      })
    }

    // Only fetch metadata if URL is valid
    if (checkIsValidVideoUrl(newUrl)) {
      // Mark as processed and debounce metadata fetching by 500ms
      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], isProcessed: true }
        }
        return updated
      })

      debounceTimersRef.current[index] = setTimeout(async () => {
        const { thumbnail, title } = await fetchVideoMetadata(newUrl)

        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail, title }
          }
          // Notify parent with updated data
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      }, 500)
    }
  }

  // Finish editing video URL - refetch metadata
  const handleFinishEditVideo = async (index: number) => {
    const video = videoUrls[index]
    if (!video?.url) return

    // Clear any pending debounce timer
    if (debounceTimersRef.current[index]) {
      clearTimeout(debounceTimersRef.current[index])
    }

    // Mark as processed and fetch metadata immediately
    const { thumbnail, title } = await fetchVideoMetadata(video.url)

    setVideoUrls(prev => {
      const updated = prev.map((v, i) =>
        i === index ? { ...v, thumbnail, title, isEditing: false, isProcessed: true } : v,
      )
      // Notify parent with updated data
      if (onVideoUrlsChange) {
        onVideoUrlsChange(updated.filter(v => v.url))
      }
      return updated
    })
  }

  // Add new video URL
  const addVideoUrl = () => {
    const newIndex = videoUrls.length
    setVideoUrls(prev => [
      ...prev,
      { id: `new-${newIndex}`, url: "", thumbnail: "", title: "", isProcessed: false },
    ])
  }

  // Remove video URL
  const removeVideoUrl = (index: number) => {
    // Don't allow deleting first empty input
    if (index === 0 && !videoUrls[0]?.url) {
      return
    }

    const updated = videoUrls.filter((_, i) => i !== index)
    setVideoUrls(updated)

    const newErrors: Record<number, string | null> = {}
    updated.forEach((_, i) => {
      if (urlErrors[i] !== undefined) {
        newErrors[i] = urlErrors[i]
      }
    })
    setUrlErrors(newErrors)

    if (onVideoUrlsChange) {
      onVideoUrlsChange(updated.filter(v => v.url))
    }
  }

  // Edit video URL - switch to edit mode
  const handleEditVideo = (index: number) => {
    setVideoUrls(prev =>
      prev.map((video, i) => (i === index ? { ...video, isEditing: true } : video)),
    )
  }

  // Handle blur on video URL input - finish editing
  const handleVideoUrlBlur = (index: number) => {
    handleFinishEditVideo(index)
  }

  // Handle thumbnail file selection
  const handleThumbnailSelect = (index: number) => {
    thumbnailInputRef.current?.click()
    // Store the index of the video for which thumbnail is being uploaded
    if (thumbnailInputRef.current) {
      ;(
        thumbnailInputRef.current as HTMLInputElement & { dataset: { videoIndex?: string } }
      ).dataset.videoIndex = index.toString()
    }
  }

  // Handle thumbnail file change and upload
  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const index = parseInt(
      (e.target as HTMLInputElement & { dataset: { videoIndex?: string } }).dataset.videoIndex ||
        "0",
      10,
    )
    const video = videoUrls[index]

    // Clear any previous error when user selects a new file
    setThumbnailError(null)

    // Validate file type (images only)
    if (!file.type.startsWith("image/")) {
      setThumbnailError("Please select an image file (JPEG, PNG, GIF, WebP)")
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setThumbnailError("Thumbnail image must be less than 5MB")
      return
    }

    setThumbnailError(null)
    setUploadingThumbnailIndex(index)

    try {
      // Check if this is an existing video (has id that's not "new-*")
      if (video.id && !video.id.startsWith("new-") && communityId) {
        // Upload to server for existing video
        const result = await uploadVideoThumbnail(communityId, video.id, file)

        // Update video with new thumbnail URL
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail: result.thumbnailUrl || "" }
          }
          return updated
        })

        toast.success("Thumbnail uploaded successfully")
      } else {
        // For new videos, create a local preview URL
        const localThumbnailUrl = URL.createObjectURL(file)
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail: localThumbnailUrl }
          }
          return updated
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to upload thumbnail"
      setThumbnailError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setUploadingThumbnailIndex(null)
    }

    // Reset input
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = ""
    }
  }

  // Sync initial video URLs only on mount (not on every change)
  const hasSyncedInitialUrls = useRef(false)
  useEffect(() => {
    if (!hasSyncedInitialUrls.current && initialVideoUrls && initialVideoUrls.length > 0) {
      setVideoUrls(initialVideoUrls)
      hasSyncedInitialUrls.current = true
    }
  }, [initialVideoUrls, initialVideoUrls.length])

  // Fetch metadata for URLs that don't have thumbnails yet
  const fetchedMetadataRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    videoUrls.forEach(async (video, index) => {
      // Skip if already fetched, has thumbnail, URL is empty, or URL is invalid
      if (
        !video.url ||
        video.thumbnail ||
        fetchedMetadataRef.current.has(video.id) ||
        video.isInvalid
      ) {
        return
      }

      // Only fetch if URL is valid
      if (checkIsValidVideoUrl(video.url)) {
        fetchedMetadataRef.current.add(video.id)

        // Mark as processed
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], isProcessed: true }
          }
          return updated
        })

        const { thumbnail, title } = await fetchVideoMetadata(video.url)

        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail, title }
          }
          // Notify parent with updated data
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      }
    })
  }, [videoUrls, onVideoUrlsChange])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(timer => {
        clearTimeout(timer)
      })
    }
  }, [])

  // Memoize images and videos arrays to prevent unnecessary re-renders
  const images: Photo[] = useMemo(
    () =>
      (community?.uploadedImages || []).map(img => ({
        id: img.id,
        src: img.filePath,
        isCover: img.isCover,
      })),
    [community?.uploadedImages],
  )

  const videos: VideoItem[] = useMemo(
    () =>
      (community?.uploadedVideos || []).map(vid => ({
        id: vid.id,
        title: vid.fileName || "Video",
        url: vid.filePath,
      })),
    [community?.uploadedVideos],
  )

  // Notify parent of media count and cover image status changes
  useEffect(() => {
    onMediaCountChange?.(images.length, videos.length)

    // Check if any image has isCover: true
    const hasCover = images.some(img => img.isCover)
    onCoverImageChange?.(hasCover)
  }, [images, videos.length, onMediaCountChange, onCoverImageChange])

  // Memoize handlers to prevent infinite re-renders
  const handleDelete = useCallback(
    (uploadId: string, type: "image" | "video") => {
      deleteImage.mutate(
        { communityId, uploadId },
        {
          onSuccess: () => {
            toast.success(`${type === "image" ? "Image" : "Video"} deleted successfully`)
          },
          onError: () => {
            toast.error(`Failed to delete ${type === "image" ? "image" : "video"}`)
          },
        },
      )
    },
    [communityId, deleteImage],
  )

  const handleSetCover = useCallback(
    (uploadId: string) => {
      setCoverImage.mutate(
        { communityId, uploadId },
        {
          onSuccess: () => {
            toast.success("Cover image updated")
          },
          onError: () => {
            toast.error("Failed to update cover image")
          },
        },
      )
    },
    [communityId, setCoverImage],
  )

  const handleModalUpload = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        setIsUploading(true)
        await uploadImage.mutateAsync({ communityId, file })
      }
      setIsUploading(false)
    },
    [communityId, uploadImage],
  )

  const handleAiImageSelected = useCallback(
    async (file: File) => {
      if (images.length >= maxFiles) {
        toast.error(`Maximum ${maxFiles} images already uploaded`)
        return
      }
      setIsUploading(true)
      try {
        await uploadImage.mutateAsync({ communityId, file })
        setAiGeneratorOpen(false)
        toast.success("AI-generated image uploaded successfully")
      } catch {
        toast.error("Failed to upload AI-generated image")
      } finally {
        setIsUploading(false)
      }
    },
    [communityId, uploadImage, images.length, maxFiles],
  )

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return

      const filesArray = Array.from(files)

      // Count images and videos in selection
      const imageFiles = filesArray.filter(file => file.type.startsWith("image/"))
      const videoFiles = filesArray.filter(file => file.type.startsWith("video/"))

      // Check image limit
      if (images.length + imageFiles.length > COMMUNITY_IMAGE_LIMITS.MAX_IMAGES) {
        toast.error(`You can only upload a maximum of ${COMMUNITY_IMAGE_LIMITS.MAX_IMAGES} images`)
        return
      }

      // Check video limit
      if (videos.length + videoFiles.length > COMMUNITY_VIDEO_LIMITS.MAX_VIDEOS) {
        toast.error(`You can only upload a maximum of ${COMMUNITY_VIDEO_LIMITS.MAX_VIDEOS} videos`)
        return
      }

      // Upload each file with validation based on type
      filesArray.forEach((file: File) => {
        if (file.type.startsWith("image/")) {
          // Validate image file type
          if (
            !COMMUNITY_IMAGE_LIMITS.ALLOWED_TYPES.includes(
              file.type as (typeof COMMUNITY_IMAGE_LIMITS.ALLOWED_TYPES)[number],
            )
          ) {
            toast.error(`Invalid file type. Please upload PNG or JPG images.`)
            return
          }

          // Validate image file size
          if (file.size > COMMUNITY_IMAGE_LIMITS.MAX_FILE_SIZE) {
            toast.error(`File size exceeds 5MB limit`)
            return
          }

          // Upload image
          setIsUploading(true)
          uploadImage.mutate(
            { communityId, file },
            {
              onSettled: () => {
                setIsUploading(false)
              },
            },
          )
        } else if (file.type.startsWith("video/")) {
          // Validate video file type
          if (
            !COMMUNITY_VIDEO_LIMITS.ALLOWED_TYPES.includes(
              file.type as (typeof COMMUNITY_VIDEO_LIMITS.ALLOWED_TYPES)[number],
            )
          ) {
            toast.error(`Invalid file type. Please upload MP4 or WebM videos.`)
            return
          }

          // Validate video file size
          if (file.size > COMMUNITY_VIDEO_LIMITS.MAX_FILE_SIZE) {
            toast.error(`File size exceeds 50MB limit`)
            return
          }

          // Upload video
          setIsUploading(true)
          uploadImage.mutate(
            { communityId, file },
            {
              onSettled: () => {
                setIsUploading(false)
              },
            },
          )
        }
      })

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    },
    [communityId, images.length, videos.length, uploadImage],
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <p className="text-body">Loading media...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,video/mp4,video/webm"
        multiple
        onChange={handleFileUpload}
        className="hidden"
        disabled={isUploading}
      />
      <input
        ref={thumbnailInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleThumbnailChange}
        className="hidden"
      />

      <div className="flex items-center gap-5">
        <button
          type="button"
          onClick={() => {
            if (images.length >= maxFiles) {
              toast.error(`Maximum ${maxFiles} images already uploaded`)
              return
            }
            setUploadModalOpen(true)
          }}
          className="flex justify-between items-center w-full border-1 border-dashed border-brand-slate-alpha-70 rounded-lg p-4 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex justify-center items-center gap-2">
            <div className="bg-brand-slate-alpha-70 p-3 flex justify-center items-center rounded-full">
              <ArrowUpToLine className="w-5 h-5 text-white" />
            </div>
            <p className="font-semibold text-primary text-[15px]">Upload Media</p>
          </div>

          <p className="text-sm text-primary">
            Up to {maxFiles} files. PNG, JPG up to {maxFileSize / (1024 * 1024)}MB
          </p>
        </button>

        {enableAiGeneration && aiEntityType && (
          <button
            type="button"
            onClick={() => setAiGeneratorOpen(true)}
            className="flex items-center gap-2.5 border border-brand-slate-alpha-70 rounded-full px-6.75 py-3 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors"
          >
            <Sparkles className="w-6 h-6 text-white" />
            <p className="font-semibold text-primary text-sm uppercase text-nowrap">
              Generate Image
            </p>
          </button>
        )}
      </div>

      {/* Thumbnail error message */}
      {thumbnailError && (
        <div className="p-2 bg-transparent border border-brand-terracotta-bright rounded-md">
          <p className="text-xs text-brand-terracotta-bright">{thumbnailError}</p>
        </div>
      )}

      {/* Images Section */}
      <div className="mt-2">
        <h3 className="font-bold text-body mb-3">Images</h3>
        {images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {images.map(image => (
              <MediaPreview
                key={image.id}
                type="image"
                src={image.src}
                isCover={image.isCover}
                onCoverSelect={() => handleSetCover(image.id)}
                onRemove={() => handleDelete(image.id, "image")}
              />
            ))}
          </div>
        )}
      </div>

      {/* Videos Section */}
      <div className="mt-2">
        <h3 className="font-bold text-body mb-3">Videos</h3>

        {/* Video URL Inputs with thumbnails */}
        <div className="space-y-3">
          {videoUrls.map((video, index) => {
            // Show input when: URL is empty OR editing OR (URL exists but not processed yet) OR URL is invalid
            const showInput = !video.url || video.isEditing || !video.isProcessed || video.isInvalid
            // Show thumbnail when: URL exists AND is processed AND not editing AND not invalid
            const showThumbnail =
              video.url && video.isProcessed && !video.isEditing && !video.isInvalid

            return (
              <div key={video.id} className="flex items-start gap-2">
                {/* Input View - show when URL is empty or editing */}
                {showInput && (
                  <>
                    <div className="flex-1">
                      <CustomInput
                        id={`videoUrl-${index}`}
                        label={`Video ${index + 1}`}
                        value={video.url}
                        onChange={e => handleVideoUrlChange(e, index)}
                        onBlur={() => handleVideoUrlBlur(index)}
                        placeholder="Enter YouTube or Vimeo URL"
                        error={urlErrors[index] || undefined}
                        onClearError={() => setUrlErrors(prev => ({ ...prev, [index]: null }))}
                      />
                    </div>
                    {/* Show trash icon only if not first empty input */}
                    {(index > 0 || video.url) && (
                      <button
                        type="button"
                        onClick={() => removeVideoUrl(index)}
                        className="mt-6 p-2 text-white"
                        title="Remove video"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </>
                )}

                {/* Thumbnail View - show when URL exists and not editing */}
                {showThumbnail && (
                  <>
                    {/* Thumbnail or Placeholder */}
                    <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-base flex items-center justify-center">
                      {video.thumbnail ? (
                        <img
                          src={video.thumbnail}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <VideoIcon className="w-10 h-10 text-body" />
                      )}
                    </div>
                    {/* Video info */}
                    <div className="flex-1 min-w-0 py-1 h-[75px] flex flex-col items-start justify-center">
                      {video.title ? (
                        <p className="text-sm font-medium text-primary truncate overflow-hidden md:w-[500px] w-[200px]">
                          {video.title}
                        </p>
                      ) : null}
                      <p className="text-xs text-body truncate overflow-hidden md:w-[500px] w-[200px]">
                        {video.url}
                      </p>
                    </div>
                    {/* Edit button */}
                    <button
                      type="button"
                      onClick={() => handleEditVideo(index)}
                      className="mt-4 p-2 text-primary  cursor-pointer"
                      title="Edit video"
                    >
                      <Pencil className="w-4.5 h-4.5" />
                    </button>
                    {/* Thumbnail upload button - between Edit and Delete */}
                    <button
                      type="button"
                      onClick={() => handleThumbnailSelect(index)}
                      className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer relative"
                      title="Upload custom thumbnail"
                      disabled={uploadingThumbnailIndex === index}
                    >
                      {uploadingThumbnailIndex === index ? (
                        <div className="w-4.5 h-4.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Image className="w-4.5 h-4.5" />
                      )}
                    </button>
                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => removeVideoUrl(index)}
                      className="mt-4 p-2 text-white cursor-pointer"
                      title="Remove video"
                    >
                      <Trash2 className="w-4.5 h-4.5" />
                    </button>
                  </>
                )}
              </div>
            )
          })}

          {/* + Add More Videos Button */}
          <button
            type="button"
            onClick={addVideoUrl}
            className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium"
          >
            <Plus className="w-5 h-5" />
            Add More
          </button>
        </div>
      </div>

      {enableAiGeneration && aiEntityType && (
        <AiImageGenerator
          open={aiGeneratorOpen}
          onOpenChange={setAiGeneratorOpen}
          entityType={aiEntityType}
          entityContext={aiEntityContext}
          onImageSelected={handleAiImageSelected}
        />
      )}

      <MediaUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUpload={handleModalUpload}
        maxFiles={maxFiles}
        maxFileSize={maxFileSize}
        allowedTypes={["image/png", "image/jpeg", "image/jpg"]}
        title="Upload Media"
        isUploading={isUploading}
        existingFilesCount={images.length}
      />
    </div>
  )
}
